import { useState } from 'react';
import { useTickets } from '@/hooks/useTickets';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { Link } from 'react-router-dom';
import { MagnifyingGlass, HandGrabbing } from '@phosphor-icons/react';
import { ticketsApi } from '@/lib/api';
import { toast } from 'sonner';
import useAppStore from '@/stores/appStore';
import {
  AgentTicketFilterPopover,
  ActiveFilterChips,
  DEFAULT_FILTERS,
} from '@/components/agent/AgentTicketFilterPopover';

export const AgentDashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';
  const { data: tickets, isLoading, refetch } = useTickets({ status: 'submitted' }, { refetchInterval: 30000 });

  const rawTickets = tickets?.data || [];

  // Multi-criteria client side filtering
  const filteredTickets = rawTickets.filter((ticket) => {
    // Search Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchNumber = ticket.ticket_number?.toLowerCase().includes(q);
      const matchCustomer = ticket.customer_name?.toLowerCase().includes(q);
      const matchPhone = ticket.customer_phone?.toLowerCase().includes(q);
      const matchProduct = ticket.product_name?.toLowerCase().includes(q);
      const matchSerial = ticket.serial_number?.toLowerCase().includes(q);
      if (!matchNumber && !matchCustomer && !matchPhone && !matchProduct && !matchSerial) {
        return false;
      }
    }

    // Priority Filter
    if (filters.priority !== 'all' && ticket.priority !== filters.priority) {
      return false;
    }

    // SLA Status Filter
    if (filters.slaStatus !== 'all' && ticket.sla_deadline) {
      const now = new Date();
      const deadline = new Date(ticket.sla_deadline);
      const diffMins = (deadline - now) / 60000;

      if (filters.slaStatus === 'breached' && diffMins > 0) return false;
      if (filters.slaStatus === 'near_breach' && (diffMins <= 0 || diffMins > 60)) return false;
      if (filters.slaStatus === 'on_track' && diffMins <= 60) return false;
    }

    // Warranty Status Filter
    if (filters.warrantyStatus !== 'all') {
      const wStatus = (ticket.warranty_status || '').toLowerCase();
      if (filters.warrantyStatus === 'active' && !wStatus.includes('active')) return false;
      if (filters.warrantyStatus === 'expired' && !wStatus.includes('expired')) return false;
    }

    // Date Range Filter
    if (filters.dateRange !== 'all' && ticket.created_at) {
      const created = new Date(ticket.created_at);
      const now = new Date();
      if (filters.dateRange === 'today') {
        if (created.toDateString() !== now.toDateString()) return false;
      } else if (filters.dateRange === 'last_7_days') {
        if (now - created > 7 * 86400 * 1000) return false;
      } else if (filters.dateRange === 'last_30_days') {
        if (now - created > 30 * 86400 * 1000) return false;
      }
    }

    return true;
  });

  // Sorting logic
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    if (filters.sortBy === 'priority_desc') {
      const priorityOrder = { urgent: 0, normal: 1, low: 2 };
      return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
    }
    if (filters.sortBy === 'created_desc') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    if (filters.sortBy === 'created_asc') {
      return new Date(a.created_at) - new Date(b.created_at);
    }

    // Default: SLA Urgency
    if (a.priority !== b.priority) {
      const priorityOrder = { urgent: 0, normal: 1, low: 2 };
      return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
    }
    return new Date(a.sla_deadline || 0) - new Date(b.sla_deadline || 0);
  });

  const handlePickup = async (e, ticketId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await ticketsApi.pickup(ticketId);
      toast.success('Ticket picked up — redirecting to review');
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick up ticket');
    }
  };

  return (
    <DashboardLayout role="agent">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black tracking-tight mb-2">Ticket Queue</h1>
          <p className="text-muted-foreground">
            Review and approve incoming service requests. Pick up a ticket to claim it for review.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border">
            <p className="label-micro mb-2">Total Pending</p>
            <p className="text-3xl font-black">{rawTickets.length}</p>
          </Card>
          <Card className="p-4 border">
            <p className="label-micro mb-2">Urgent Priority</p>
            <p className="text-3xl font-black text-red-500">
              {rawTickets.filter((t) => t.priority === 'urgent').length}
            </p>
          </Card>
          <Card className="p-4 border">
            <p className="label-micro mb-2">SLA Breached</p>
            <p className="text-3xl font-black text-yellow-600">
              {rawTickets.filter((t) => t.sla_deadline && new Date(t.sla_deadline) < new Date()).length}
            </p>
          </Card>
          <Card className="p-4 border">
            <p className="label-micro mb-2 font-bold text-emerald-700">Available (Filtered)</p>
            <p className="text-3xl font-black text-emerald-600">{sortedTickets.length}</p>
          </Card>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-4 mb-3">
          <div className="flex-1 relative">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder="Search by ticket number, customer name, phone, or product..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <AgentTicketFilterPopover filters={filters} setFilters={setFilters} />
        </div>

        {/* Active Filter Chips */}
        <ActiveFilterChips filters={filters} setFilters={setFilters} />

        {/* Tickets Table */}
        {isLoading ? (
          <LoadingSpinner />
        ) : sortedTickets.length > 0 ? (
          <div className="space-y-3">
            {sortedTickets.map((ticket) => (
              <Link key={ticket.id} to={`/ops/agent/tickets/${ticket.id}`}>
                <Card
                  className="p-4 hover:bg-muted/30 transition-all cursor-pointer border"
                  data-testid={`ticket-row-${ticket.ticket_number}`}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Ticket Info */}
                    <div className="col-span-3">
                      <p className="font-bold mb-1">{ticket.ticket_number}</p>
                      <p className="text-sm text-muted-foreground">{ticket.customer_name}</p>
                    </div>

                    {/* Product */}
                    <div className="col-span-3">
                      <p className="font-medium text-sm">{ticket.product_name}</p>
                      <p className="text-xs text-muted-foreground">{ticket.issue_category}</p>
                    </div>

                    {/* Priority */}
                    <div className="col-span-2">
                      <PriorityBadge priority={ticket.priority} />
                    </div>

                    {/* SLA Timer */}
                    <div className="col-span-2">
                      <SlaTimer deadline={ticket.sla_deadline} />
                    </div>

                    {/* Pick Up Action */}
                    <div className="col-span-2 text-right">
                      <Button
                        size="sm"
                        onClick={(e) => handlePickup(e, ticket.id)}
                        className="font-bold text-xs uppercase tracking-wider text-white hover:opacity-90 shadow-sm"
                        style={{ backgroundColor: brandColorAccent }}
                        data-testid={`pickup-btn-${ticket.ticket_number}`}
                      >
                        <HandGrabbing size={16} weight="bold" className="mr-1" />
                        Pick Up
                      </Button>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center border-dashed">
            <p className="text-muted-foreground font-medium">No tickets match your search or filter criteria</p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};